import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { MdChat, MdMic, MdMicOff } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import styles from "./VoicePopup.module.css";

// Audio Settings
const SAMPLE_RATE = 48000;
const CHUNK_MS = 100;
const AUDIO_THRESHOLD = 0.003;
const SILENCE_DURATION = 800;

// Convert Float32Array → 16-bit PCM
function floatTo16BitPCM(float32Array) {
  const length = float32Array.length;
  const buffer = new ArrayBuffer(length * 2);
  const view = new DataView(buffer);
  for (let i = 0; i < length; i++) {
    const sample = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(i * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return buffer;
}

// Audio Player Class
class AudioPlayer {
  constructor() {
    this.audioContext = null;
    this.queue = [];
    this.nextTime = 0;
    this.isPlaying = false;
  }

  async init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
    }
  }

  async playAudio(arrayBuffer) {
    await this.init();
    const int16Array = new Int16Array(arrayBuffer);
    const float32Array = new Float32Array(int16Array.length);
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768;
    }
    this.queue.push(float32Array);
    this.schedule();
  }

  schedule() {
    if (!this.audioContext) return;
    if (!this.isPlaying) {
      this.nextTime = Math.max(this.audioContext.currentTime, this.nextTime || 0);
      this.isPlaying = true;
    }
    while (this.queue.length > 0) {
      const chunk = this.queue.shift();
      const buffer = this.audioContext.createBuffer(1, chunk.length, this.audioContext.sampleRate);
      buffer.getChannelData(0).set(chunk);
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.audioContext.destination);
      source.start(this.nextTime);
      this.nextTime += buffer.length / this.audioContext.sampleRate;
    }
    if (this.queue.length === 0) this.isPlaying = false;
  }

  clear() {
    this.queue = [];
    if (this.audioContext) this.nextTime = this.audioContext.currentTime + 0.1;
  }
}

const VoicePopup = ({ selectedAgent }) => {
  const [isActive, setIsActive] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isChatMode, setIsChatMode] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);


  console.log("Selected Agent in VoicePopup:", selectedAgent);

  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");

  const [caseWs, setCaseWs] = useState(null);

  const [sessionId] = useState(uuidv4());
  const prevAgentIdRef = useRef(null);
  const wsRef = useRef(null);
  const playerRef = useRef(new AudioPlayer());
  const micRef = useRef({
    stream: null,
    audioContext: null,
    processor: null,
    source: null,
    running: false,
  });

  // Connect to your Mutation Bot (यह आपका मेन चैट बॉट है)
  const connectToBot = useCallback(() => {
    if (caseWs) {
      caseWs.close();
      setCaseWs(null);
    }

    if (!selectedAgent?.agentId) return;

    const ws = new WebSocket("wss://prod.toxboxai.com/ws/case");

    ws.onopen = () => {
      console.log("Bot Connected - Fresh Session");
      setCaseWs(ws);
      setMessages([]); // बिल्कुल खाली चैट
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "bot") {
          setMessages((prev) => [...prev, { text: data.message, sender: "bot" }]);
        }
      } catch (err) {
        // ignore binary/audio data
      }
    };

    ws.onclose = () => {
      console.log("Bot Disconnected");
      setCaseWs(null);
    };

    ws.onerror = (err) => {
      console.error("WebSocket Error:", err);
    };
  }, [caseWs, selectedAgent?.agentId]);

  // Send message to bot
  const sendMessage = (text) => {
    if (caseWs?.readyState === WebSocket.OPEN && text.trim()) {
      caseWs.send(text);
      setMessages((prev) => [...prev, { text, sender: "user" }]);
      setInputMessage("");
    }
  };

  // Voice Chat Start
  const startVoiceChat = async () => {
    if (!selectedAgent?.agentId) return;
    try {
      const res = await fetch("https://prod.toxboxai.com/api/start-voice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agentId: selectedAgent.agentId }),
      });
      if (!res.ok) throw new Error("Voice chat failed");

      const ws = new WebSocket("ws://localhost:8081");
      ws.binaryType = "arraybuffer";

      ws.onopen = () => setIsConnected(true);
      ws.onmessage = async (event) => {
        if (typeof event.data === "string") {
          try {
            const data = JSON.parse(event.data);
            if (data.source === "server" && data.event === "call_ended") stopVoiceChat();
            if (data.source === "Provaantech" && data.event?.type === "playback_clear_buffer") {
              playerRef.current.clear();
              setIsListening(false);
            }
          } catch (e) { }
        } else if (event.data.byteLength > 0) {
          await playerRef.current.playAudio(event.data);
        }
      };
      ws.onclose = () => setIsConnected(false);
      ws.onerror = () => { };

      wsRef.current = ws;
      await startMicrophone();
      setIsActive(true);
    } catch (err) {
      console.error("Voice Error:", err);
    }
  };

  // Start Microphone
  const startMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: SAMPLE_RATE,
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: true,
        },
      });

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === "suspended") await audioContext.resume();

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      let chunkBuffers = [];
      const chunkSamplesTarget = Math.round((CHUNK_MS / 1000) * audioContext.sampleRate);
      let lastSpeechTime = 0;
      let lastCommitTime = 0;

      processor.onaudioprocess = (e) => {
        if (!micRef.current.running) return;
        const input = e.inputBuffer.getChannelData(0);
        const rms = Math.sqrt(input.reduce((s, v) => s + v * v, 0) / input.length);
        setAudioLevel(rms);

        if (rms > AUDIO_THRESHOLD) {
          lastSpeechTime = Date.now();
          if (!isSpeaking) {
            setIsSpeaking(true);
            setIsListening(false);
          }
        }

        chunkBuffers.push(new Float32Array(input));
        if (chunkBuffers.reduce((s, b) => s + b.length, 0) >= chunkSamplesTarget) {
          const merged = new Float32Array(chunkBuffers.reduce((s, b) => s + b.length, 0));
          let offset = 0;
          for (const buf of chunkBuffers) {
            merged.set(buf, offset);
            offset += buf.length;
          }
          chunkBuffers = [];
          const pcm = floatTo16BitPCM(merged);
          if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(pcm);

          const now = Date.now();
          if (isSpeaking && now - lastSpeechTime > SILENCE_DURATION && now - lastCommitTime > 500) {
            lastCommitTime = now;
            setIsSpeaking(false);
            setIsListening(true);
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: "commit" }));
            }
          }
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
      micRef.current = { stream, audioContext, processor, source, running: true };
    } catch (err) {
      console.error("Mic Error:", err);
    }
  };

  // Stop Voice Chat
  const stopVoiceChat = async () => {
    if (micRef.current.running) {
      const { stream, audioContext, processor, source } = micRef.current;
      processor?.disconnect();
      source?.disconnect();
      stream?.getTracks().forEach((t) => t.stop());
      audioContext?.close();
      micRef.current.running = false;
    }
    playerRef.current.clear();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    try {
      await fetch("https://prod.toxboxai.com/api/end-voice", { method: "POST", body: JSON.stringify({}) });
    } catch (e) { }
    setIsActive(false);
    setIsConnected(false);
    setIsSpeaking(false);
    setIsListening(false);
    setAudioLevel(0);
  };

  // Cleanup on Unmount
  useEffect(() => {
    return () => {
      stopVoiceChat();
      if (caseWs) {
        caseWs.close();
        setCaseWs(null);
      }
      playerRef.current.clear();
    };
  }, []);

  // Reconnect when agent changes
  useEffect(() => {
    if (isChatMode && selectedAgent?.agentId !== prevAgentIdRef.current) {
      connectToBot();
    }
    prevAgentIdRef.current = selectedAgent?.agentId;
  }, [selectedAgent?.agentId, isChatMode, connectToBot]);

  return (
    <div
      className={styles.chatbotContainer}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.chatbotWrapper}>
        <div className={styles.contentWrapper}>
          {/* Status Dot */}
          <div style={{ position: "absolute", top: "10px", left: "10px" }}>
            {isConnected ? (
              <div
                style={{
                  borderRadius: "50%",
                  height: "10px",
                  width: "10px",
                  background: isSpeaking ? "#4CAF50" : isListening ? "#2196F3" : "#666",
                  boxShadow: "0 0 10px currentColor",
                }}
              />
            ) : (
              <div style={{ borderRadius: "50%", height: "10px", width: "10px", background: "#ff2727" }} />
            )}
          </div>

          <div className={styles.imageWrapper}>
            <img src={selectedAgent?.imageUrl} alt="Agent" className={styles.avatarImage} />
          </div>

          <div className={`${styles.nameWrapper} ${isHovered ? styles.visible : ""}`}>
            <p className={styles.agentName}>Chat with {selectedAgent?.name}</p>
          </div>

          {/* Mic Button (Voice Mode) */}
          {!isChatMode && (
            <button
              className={`${styles.micButton} ${isMicOn ? styles.pulse : ""}`}
              onClick={() => setIsMicOn(!isMicOn)}
            >
              <div className={styles.waveCircleWrapper}>
                {isMicOn && <div className={styles.waveAnimation}></div>}
              </div>
              <div className={styles.micIcon}>
                {isMicOn ? (
                  <MdMic className={styles.micIconOverlay} onClick={startVoiceChat} />
                ) : (
                  <MdMicOff className={styles.micIconOverlay} onClick={stopVoiceChat} />
                )}
              </div>
            </button>
          )}

          {/* Toggle Chat Mode */}
          <button
            className={styles.modeToggleButton}
            onClick={() => {
              setIsChatMode(!isChatMode);
              if (!isChatMode) {
                connectToBot(); // चैट खुलते ही कनेक्ट हो जाए
              }
            }}
          >
            {isChatMode ? <MdMic className={styles.modeIcon} /> : <MdChat className={styles.modeIcon} />}
          </button>
        </div>

        {/* TEXT CHAT - बिल्कुल खाली शुरू */}
        {isChatMode && (
          <div className={styles.chatPopup}>
            <div className={styles.chatHeader}>
              <span className={styles.headerAgentName}>{selectedAgent?.name || "Assistant"}</span>

              {/* Close Button */}
              <button
                onClick={() => {
                  setIsChatMode(false);
                  setMessages([]);
                  if (caseWs) {
                    caseWs.close();
                    setCaseWs(null);
                  }
                }}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "10px",
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  fontSize: "30px",
                  cursor: "pointer",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            {/* Messages Area */}
            <div className={styles.chatMessages}>
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`${styles.message} ${msg.sender === "user" ? styles.userMessage : styles.agentMessage}`}
                >
                  {msg.text}
                </div>
              ))}
            </div>

            {/* Input Box */}
            <div className={styles.chatInputWrapper}>
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && inputMessage.trim()) {
                    sendMessage(inputMessage);
                  }
                }}
                className={styles.chatInput}
                placeholder="Type your message..."
                autoFocus
              />
              <button
                onClick={() => inputMessage.trim() && sendMessage(inputMessage)}
                className={styles.sendButton}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoicePopup;